import type {DownloadUsageView} from "account/downloadUsage";
import type {GroupResponse} from "contacts/groups/GroupApi";
import {recipientEmails, recipientSelection} from "./createFormLogic";

/**
 * The send screen's download-limit card (kitecorp/transferify-ui#2468): what it says and how far its
 * meter fills, from three numbers — the month's used and limit figures and the recipients on the
 * transfer being composed.
 *
 * Pure domain, no React (epic #26). The 80% threshold is NOT restated here: `account/downloadUsage`
 * already decides the usage level, and this module only adds the second trigger the composer knows
 * about — more recipients than downloads left. A sender sharing one set of photos with thirty people
 * spends thirty downloads, and this is the one moment they can still see that coming.
 */

export type DownloadLimitCardState = "near" | "reached";

export interface DownloadLimitCardModel {
	state: DownloadLimitCardState;
	used: number;
	limit: number;
	recipients: number;
	/** The solid meter fill, 0–100: used / limit, capped so the bar cannot overrun its track. */
	usedPercent: number;
	/** The striped fill after it, 0–100: the recipients that still fit this month, min(recipients, limit − used). */
	pendingPercent: number;
	/** The recipients this month can no longer serve: recipients − what fits. */
	shortfall: number;
}

/**
 * The card's model, or null while there is nothing to warn about: no usage data, or usage under the
 * warning threshold with every entered recipient still fitting.
 *
 * <b>The boundary is deliberately "more than", never "as many as".</b> A sender with exactly as many
 * recipients as downloads left can still serve them all, and warning them is crying wolf on the one
 * transfer that fits.
 */
export function downloadLimitCard(view: DownloadUsageView | null, recipients: number): DownloadLimitCardModel | null {
	if (view === null) {
		return null;
	}
	const left = Math.max(0, view.limit - view.used);
	const outrun = recipients > left;
	if (view.level === "ok" && !outrun) {
		return null;
	}
	const fits = Math.min(recipients, left);
	return {
		state: view.level === "reached" ? "reached" : "near",
		used: view.used,
		limit: view.limit,
		recipients,
		// Integer arithmetic first: 41 * 100 / 50 is exactly 82, where 41 / 50 * 100 is not.
		usedPercent: Math.min(100, (view.used * 100) / view.limit),
		pendingPercent: (fits * 100) / view.limit,
		shortfall: recipients - fits,
	};
}

/** The catalog KEY for the card's title; the React layer looks up the locale's copy (ui#334 pattern). */
export function limitCardTitleKey(state: DownloadLimitCardState): "nearTitle" | "reachedTitle" {
	return state === "reached" ? "reachedTitle" : "nearTitle";
}

/**
 * How many people this transfer would notify: the composer's direct chips and its selected groups'
 * members, a person addressed both ways counted once — the same union the recipient cap reads (ui#210).
 */
export function composerRecipientCount(emails: readonly string[], groups: readonly GroupResponse[]): number {
	return recipientEmails(recipientSelection(emails, groups)).length;
}
